import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Badge } from "@/design-system/Badge";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { useBool } from "@/hooks/useBool";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { ANALYTICS_TABLE_PAGE_SIZE } from "@/modules/analytics/constants";
import type {
  EventAnalyticsDateRange,
  EventAnalyticsEventRow,
  EventAnalyticsEventType,
  EventAnalyticsOccurrenceMode,
  EventAnalyticsOrigin,
  EventAnalyticsSeries,
} from "@/queries/analytics/event-reporting-types";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { hasMultipleEventOrigins } from "./event-origins";
import { EventAnalyticsContent } from "./EventAnalyticsContent";
import { EventAnalyticsFilterDrawer, type EventAnalyticsFilterValues } from "./EventAnalyticsFilterDrawer";

export interface EventAnalyticsDateRangeOption {
  value: EventAnalyticsDateRange;
  name: string;
}

/**
 * Shows the story-led event view in `focused` mode and every available event insight in `all` mode.
 * Defaults to `focused` when no mode is provided.
 */
export function EventAnalyticsPage({ mode = "focused" }: { mode?: "focused" | "all" }): React.ReactNode {
  const { i18n, t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const analyticsScope = useAnalyticsScope();
  const [selectedDateRange, setSelectedDateRange] = useState<EventAnalyticsDateRange>("last6Months");
  const [selectedEventType, setSelectedEventType] = useState<EventAnalyticsEventType>("regular");
  const [occurrenceMode, setOccurrenceMode] = useState<EventAnalyticsOccurrenceMode>("occurrences");
  const [selectedSeriesId, setSelectedSeriesId] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<EventAnalyticsFilterValues>({
    origin: "all",
    category: "all",
    status: "all",
  });
  const [isFilterOpened, filterOpenHandlers] = useBool();

  const dateRangeOptions = useMemo<NonEmptyArray<EventAnalyticsDateRangeOption>>(
    () => [
      {
        value: "last6Months",
        name: t("common.date-range.last-months", { count: 6 }),
      },
      {
        value: "lastYear",
        name: t("common.date-range.last-year"),
      },
    ],
    [t],
  );
  const selectedDateRangeOption = dateRangeOptions.find((option) => option.value === selectedDateRange)!;
  const query = {
    dateRange: selectedDateRange,
    eventType: selectedEventType,
    origin: selectedEventType === "regular" && filter.origin !== "all" ? filter.origin : undefined,
    category: filter.category === "all" ? undefined : filter.category,
    status: filter.status === "all" ? undefined : filter.status,
  };
  const projectResult = useQuery({
    ...analyticsQueries.eventReporting(query),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "project",
  });
  const portfolioResult = useQuery({
    ...analyticsQueries.portfolioEventReporting({
      ...query,
      projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [],
    }),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "portfolio" && !analyticsScope.isLoading,
  });
  const { data, isLoading, isFetching, error } = analyticsScope.type === "portfolio" ? portfolioResult : projectResult;
  const showOriginFilter = selectedEventType === "regular" && (data?.originCounts.external ?? 0) > 0;
  const showOriginColumn = selectedEventType === "regular" && hasMultipleEventOrigins(data?.originCounts);

  const filteredEvents = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const visibleEvents = (data?.events || []).filter((event) => event.status !== ("removed" as string));

    if (!normalizedSearch) {
      return visibleEvents;
    }

    return visibleEvents.filter((event) => event.name.toLocaleLowerCase().includes(normalizedSearch));
  }, [data?.events, search]);
  const pageCount = Math.max(1, Math.ceil(filteredEvents.length / ANALYTICS_TABLE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paginatedEvents = filteredEvents.slice(
    currentPage * ANALYTICS_TABLE_PAGE_SIZE,
    (currentPage + 1) * ANALYTICS_TABLE_PAGE_SIZE,
  );
  const effectiveSelectedSeriesId =
    selectedSeriesId === "all" || data?.recurring.series.some((series) => series.id === selectedSeriesId)
      ? selectedSeriesId
      : "all";

  const eventColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<EventAnalyticsEventRow>();

    return [
      helper.accessor("name", {
        header:
          selectedEventType === "announcement"
            ? t("page.analytics-events.table.announcement")
            : t("page.analytics-events.table.event"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("origin", {
        header: t("page.analytics-events.table.origin"),
        cell: (cell) => <Badge variant="special">{getOriginLabel(t, cell.getValue())}</Badge>,
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.accessor("startsAt", {
        header: t("page.analytics-events.table.date"),
        cell: (cell) => <span>{formatDate(i18n, "datetimeShort", cell.getValue())}</span>,
        minSize: TABLE_COLUMN_SIZES.DATE_TIME.MIN,
        size: TABLE_COLUMN_SIZES.DATE_TIME.DEFAULT,
      }),
      helper.accessor("recurrence", {
        header: t("page.analytics-events.table.recurrence"),
        cell: (cell) => <span>{getRecurrenceLabel(t, cell.getValue())}</span>,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("registrations", {
        header: t("page.analytics-events.table.registrations"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("guests", {
        header: t("page.analytics-events.table.guests"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("capacity", {
        header: t("page.analytics-events.table.capacity"),
        cell: (cell) => <span>{cell.getValue() ?? "-"}</span>,
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("status", {
        header: t("page.analytics-events.table.status"),
        cell: (cell) => (
          <Badge variant={getStatusBadgeVariant(cell.getValue())}>{getStatusLabel(t, cell.getValue())}</Badge>
        ),
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
    ];
  }, [i18n, selectedEventType, t]);
  const eventTable = useResizableTable(TABLE_KEYS.ANALYTICS_EVENTS, {
    columns: eventColumns,
    data: paginatedEvents,
    state: {
      columnVisibility: {
        origin: showOriginColumn,
        recurrence: selectedEventType === "regular",
        registrations: selectedEventType === "regular",
        guests: selectedEventType === "regular",
        capacity: selectedEventType === "regular",
      },
    },
    initialState: {
      sorting: [{ id: "startsAt", desc: true }],
    },
  });

  const seriesColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<EventAnalyticsSeries>();

    return [
      helper.accessor("name", {
        header: t("page.analytics-events.series-table.series"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("frequency", {
        header: t("page.analytics-events.series-table.frequency"),
        cell: (cell) => <span>{getRecurrenceLabel(t, cell.getValue())}</span>,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("occurrences", {
        header: t("page.analytics-events.series-table.occurrences"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("registrations", {
        header: t("page.analytics-events.series-table.registrations"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("averageRegistrations", {
        header: t("page.analytics-events.series-table.average"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("zeroRegistrationOccurrences", {
        header: t("page.analytics-events.series-table.without-registrations"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
    ];
  }, [t]);
  const seriesTable = useResizableTable(TABLE_KEYS.ANALYTICS_EVENT_SERIES, {
    columns: seriesColumns,
    data: data?.recurring.series || [],
  });

  const activeFilterCount =
    Number(showOriginFilter && filter.origin !== "all") +
    Number(filter.category !== "all") +
    Number(filter.status !== "all");
  const handleDateRangeChange = (option: EventAnalyticsDateRangeOption) => {
    setSelectedDateRange(option.value);
    setFilter((current) => ({ ...current, origin: "all" }));
    setPage(0);
  };
  const handleEventTypeChange = (eventType: EventAnalyticsEventType) => {
    setSelectedEventType(eventType);
    if (eventType === "announcement") {
      setOccurrenceMode("occurrences");
    }
    setSelectedSeriesId("all");
    setPage(0);
  };
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };
  const handleFilterUpdate = (value: EventAnalyticsFilterValues) => {
    setFilter(value);
    setPage(0);
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <EventAnalyticsContent
        mode={mode}
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        selectedDateRange={selectedDateRangeOption}
        dateRangeOptions={dateRangeOptions}
        onDateRangeChange={handleDateRangeChange}
        selectedEventType={selectedEventType}
        onEventTypeChange={handleEventTypeChange}
        occurrenceMode={occurrenceMode}
        onOccurrenceModeChange={setOccurrenceMode}
        selectedSeriesId={effectiveSelectedSeriesId}
        onSeriesChange={setSelectedSeriesId}
        search={search}
        onSearchChange={handleSearchChange}
        activeFilterCount={activeFilterCount}
        onOpenFilters={filterOpenHandlers.setTrue}
        eventTable={eventTable}
        seriesTable={seriesTable}
        page={currentPage}
        pageCount={pageCount}
        resultCount={filteredEvents.length}
        onPageChange={setPage}
      />
      <EventAnalyticsFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filterOpenHandlers.set}
        eventType={selectedEventType}
        filter={filter}
        showOriginFilter={showOriginFilter}
        onUpdate={handleFilterUpdate}
      />
    </>
  );
}

function getOriginLabel(
  t: ReturnType<typeof useTranslation>["t"],
  origin: Exclude<EventAnalyticsOrigin, "all">,
): string {
  return t(`page.analytics-events.filters.origin.${origin}` as const);
}

function getRecurrenceLabel(
  t: ReturnType<typeof useTranslation>["t"],
  recurrence: EventAnalyticsEventRow["recurrence"] | EventAnalyticsSeries["frequency"],
): string {
  return t(`page.analytics-events.recurrence.${recurrence}` as const);
}

function getStatusLabel(t: ReturnType<typeof useTranslation>["t"], status: EventAnalyticsEventRow["status"]): string {
  return t(`page.analytics-events.filters.status.${status}` as const);
}

function getStatusBadgeVariant(status: EventAnalyticsEventRow["status"]): "success" | "default" {
  if (status === "active") {
    return "success";
  }

  return "default";
}
